import { Suspense, useState } from "react";
import { Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import type { AspectRatio, GenerateContentV2Dto, ReferenceMode } from "shared";
import { useEventCallback } from "usehooks-ts";
import { useForm } from "../../common/form/useForm";
import { Modal, useModal } from "../../common/modal/Modal";
import { ExtraForm } from "../../common/pure/ExtraFormGroup";
import { useCompany } from "../../company/useCompany";
import { Button } from "../../components/Button";
import { ContentLoading } from "../../components/ContentLoading";
import { LinkGen } from "../../LinkGen";
import { useMediaActions } from "../useMediaActions";
import ReferenceImageSelector from "./ReferenceImageSelector";

type ViewState = "form" | "generating" | "preview";

interface FormValues {
	prompt: string;
	aspectRatio: AspectRatio;
	includeLogo: boolean;
	includeBrandColors: boolean;
	caption: string;
	referenceImageId: number | undefined;
	referenceMode: ReferenceMode | undefined;
}

export function ModalGenerateImagesV2() {
	const { close } = useModal();
	const navigate = useNavigate();
	const { generateV2, saveGeneratedV2, invalidateMedia } = useMediaActions();
	const company = useCompany();

	const [viewState, setViewState] = useState<ViewState>("form");
	const [generatedImageUrl, setGeneratedImageUrl] = useState<string>("");
	const [generatedPrompt, setGeneratedPrompt] = useState<string>("");

	const hasLogo = !!company.logo_url;
	const hasBrandColors =
		company.brand_colors && company.brand_colors.length > 0;

	const form = useForm({
		initialValues: {
			prompt: "",
			aspectRatio: "1:1",
			includeLogo: false,
			includeBrandColors: false,
			caption: "",
			referenceImageId: undefined,
			referenceMode: undefined,
		} satisfies FormValues,
		onSubmit: async (values) => {
			setViewState("generating");
			try {
				const dto: GenerateContentV2Dto = {
					prompt: values.prompt,
					aspectRatio: values.aspectRatio,
					includeLogo: values.includeLogo,
					includeBrandColors: values.includeBrandColors,
					caption: values.caption || undefined,
					referenceImageId: values.referenceImageId,
					referenceMode: values.referenceMode,
				};

				const result = await generateV2.mutateAsync(dto);
				setGeneratedImageUrl(result.imageUrl);
				setGeneratedPrompt(result.prompt);
				setViewState("preview");
			} catch {
				setViewState("form");
			}
		},
	});

	const handleRegenerate = useEventCallback(() => {
		setViewState("form");
		setGeneratedImageUrl("");
		setGeneratedPrompt("");
	});

	const handleSaveToLibrary = useEventCallback(async () => {
		await saveGeneratedV2.mutateAsync(generatedImageUrl);
		await invalidateMedia();
		close();
	});

	const handleCreatePost = useEventCallback(async () => {
		const result = await saveGeneratedV2.mutateAsync(generatedImageUrl);
		await invalidateMedia();
		close();
		navigate(`${LinkGen.PostCreate()}?mediaId=${result.mediaId}`);
	});

	const handleReferenceIdChange = useEventCallback((id: number | undefined) => {
		form.setFieldValue("referenceImageId", id);
		if (id && !form.values.referenceMode) {
			form.setFieldValue("referenceMode", "inspiration");
		}
		if (!id) {
			form.setFieldValue("referenceMode", undefined);
		}
	});

	const handleReferenceModeChange = useEventCallback((mode: ReferenceMode) => {
		form.setFieldValue("referenceMode", mode);
	});

	return (
		<Modal size="lg">
			{viewState === "form" && (
				<Form onSubmit={form.handleSubmit}>
					<ExtraForm.Group label="Prompt">
						<ExtraForm.Control
							as="textarea"
							rows={3}
							placeholder="Describe the image you want to generate..."
							{...form.getFieldProps("prompt")}
							{...form.getFieldState("prompt")}
						/>
					</ExtraForm.Group>

					<div className="mb-3">
						<span className="form-label d-block">Aspect Ratio</span>
						<div className="d-flex gap-2">
							{[
								{ value: "9:16", label: "Story (9:16)" },
								{ value: "4:5", label: "Feed (4:5)" },
								{ value: "1:1", label: "Square (1:1)" },
							].map((option) => (
								<Form.Check
									key={option.value}
									type="radio"
									id={`aspectRatio-${option.value}`}
									name="aspectRatio"
									label={option.label}
									checked={form.values.aspectRatio === option.value}
									onChange={() =>
										form.setFieldValue(
											"aspectRatio",
											option.value as AspectRatio,
										)
									}
								/>
							))}
						</div>
					</div>

					<div className="mb-3">
						<Form.Check
							type="checkbox"
							id="includeLogo"
							label="Include company logo"
							checked={form.values.includeLogo}
							onChange={(e) =>
								form.setFieldValue("includeLogo", e.target.checked)
							}
							disabled={!hasLogo}
						/>
						{!hasLogo && (
							<small className="text-muted d-block mt-1">
								No logo uploaded for this company
							</small>
						)}
					</div>

					<div className="mb-3">
						<Form.Check
							type="checkbox"
							id="includeBrandColors"
							label="Include brand colors"
							checked={form.values.includeBrandColors}
							onChange={(e) =>
								form.setFieldValue("includeBrandColors", e.target.checked)
							}
							disabled={!hasBrandColors}
						/>
						{!hasBrandColors && (
							<small className="text-muted d-block mt-1">
								No brand colors set for this company
							</small>
						)}
						{hasBrandColors && (
							<div className="d-flex gap-1 mt-1">
								{company.brand_colors?.map((color) => (
									<div
										key={color}
										style={{
											width: "20px",
											height: "20px",
											backgroundColor: color,
											borderRadius: "4px",
											border: "1px solid #ccc",
										}}
										title={color}
									/>
								))}
							</div>
						)}
					</div>

					<ExtraForm.Group label="Caption (optional)">
						<ExtraForm.Control
							placeholder="Text to overlay on the image"
							{...form.getFieldProps("caption")}
							{...form.getFieldState("caption")}
						/>
						<small className="text-muted">
							This text will be displayed on the generated image
						</small>
					</ExtraForm.Group>

					<div className="mb-3">
						<span className="form-label d-block">
							Reference Image (optional)
						</span>
						<Suspense fallback={<ContentLoading />}>
							<ReferenceImageSelector
								selectedId={form.values.referenceImageId}
								mode={form.values.referenceMode}
								onSelectId={handleReferenceIdChange}
								onModeChange={handleReferenceModeChange}
							/>
						</Suspense>
					</div>

					<Button
						loading={generateV2.isPending}
						icon="bolt"
						className="mt-3"
						full
						type="submit"
						disabled={!form.values.prompt.trim()}
					>
						Generate Image
					</Button>
				</Form>
			)}

			{viewState === "generating" && (
				<div className="text-center py-5">
					<ContentLoading />
					<p className="mt-3">Generating your image...</p>
				</div>
			)}

			{viewState === "preview" && (
				<div>
					<div className="text-center mb-4">
						<img
							src={generatedImageUrl}
							alt="Generated preview"
							style={{
								maxWidth: "100%",
								maxHeight: "400px",
								borderRadius: "8px",
							}}
						/>
					</div>

					{generatedPrompt !== form.values.prompt && (
						<div className="mb-3">
							<small className="text-muted">
								<strong>Enhanced prompt:</strong> {generatedPrompt}
							</small>
						</div>
					)}

					<div className="d-flex flex-column gap-2">
						<Button
							variant="ghost"
							icon="refresh"
							full
							onClick={handleRegenerate}
						>
							Regenerate
						</Button>
						<Button
							icon="cloud_upload"
							full
							onClick={handleSaveToLibrary}
							loading={saveGeneratedV2.isPending}
						>
							Save to Media Library
						</Button>
						<Button
							variant="primary"
							icon="edit"
							full
							onClick={handleCreatePost}
							loading={saveGeneratedV2.isPending}
						>
							Create Post with this Image
						</Button>
					</div>
				</div>
			)}
		</Modal>
	);
}
